import React, { useState, useRef, useEffect } from 'react';
import {
    Search, Bell, Plus, ChevronDown, Menu,
    Settings, LogOut, User as UserIcon, HelpCircle,
    PanelLeftClose, PanelLeftOpen, Filter,
    Calendar, KanbanSquare, ListTodo
} from 'lucide-react';
import { User, ViewMode } from '../../types';

interface BordioHeaderProps {
    onSearchChange: (query: string) => void;
    currentUser: User | null;
    currentView: ViewMode;
    onProfileClick: () => void;
    onSettingsClick: () => void;
    onMenuClick: () => void;
    onLogout: () => void;
    onCreateIssue: () => void;
    onToggleSidebar?: () => void;
    sidebarCollapsed?: boolean;
}

export const BordioHeader: React.FC<BordioHeaderProps> = ({
    onSearchChange,
    currentUser,
    currentView,
    onProfileClick,
    onSettingsClick,
    onMenuClick,
    onLogout,
    onCreateIssue,
    onToggleSidebar,
    sidebarCollapsed,
}) => {
    const [searchFocused, setSearchFocused] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showNotifications, setShowNotifications] = useState(false);
    const userMenuRef = useRef<HTMLDivElement>(null);
    const notifRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
                setShowUserMenu(false);
            }
            if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
                setShowNotifications(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const getViewTitle = () => {
        const titles: Record<string, string> = {
            'dashboard': 'Dashboard',
            'boards': 'Board',
            'calendar': 'Calendar',
            'issues': 'Tasks',
            'sprints': 'Sprints',
            'projects': 'Projects',
            'team-analytics': 'Analytics',
            'reports': 'Reports',
            'filters': 'Filters',
        };
        return titles[currentView] || 'Dashboard';
    };

    const getViewIcon = () => {
        const icons: Record<string, React.ReactNode> = {
            'dashboard': <KanbanSquare className="w-5 h-5" />,
            'boards': <KanbanSquare className="w-5 h-5" />,
            'calendar': <Calendar className="w-5 h-5" />,
            'issues': <ListTodo className="w-5 h-5" />,
        };
        return icons[currentView] || null;
    };

    return (
        <header className="h-14 bg-white border-b border-surface-200 flex items-center px-4 gap-3 flex-shrink-0">
            {/* Mobile menu button */}
            <button
                onClick={onMenuClick}
                className="md:hidden btn-ghost p-2"
            >
                <Menu className="w-5 h-5" />
            </button>

            {/* Sidebar toggle */}
            {onToggleSidebar && (
                <button
                    onClick={onToggleSidebar}
                    className="hidden md:flex btn-ghost p-2"
                    title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                >
                    {sidebarCollapsed ? (
                        <PanelLeftOpen className="w-4.5 h-4.5 text-surface-500" />
                    ) : (
                        <PanelLeftClose className="w-4.5 h-4.5 text-surface-500" />
                    )}
                </button>
            )}

            {/* Page title */}
            <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-surface-900">{getViewTitle()}</h2>
            </div>

            {/* Spacer */}
            <div className="flex-1" />

            {/* Search */}
            <div className={`relative hidden md:flex items-center transition-all duration-300 ${searchFocused ? 'w-80' : 'w-56'
                }`}>
                <Search className="absolute left-3 w-4 h-4 text-surface-400 pointer-events-none" />
                <input
                    type="text"
                    placeholder="Search tasks, projects..."
                    value={searchQuery}
                    onChange={(e) => {
                        setSearchQuery(e.target.value);
                        onSearchChange(e.target.value);
                    }}
                    onFocus={() => setSearchFocused(true)}
                    onBlur={() => setSearchFocused(false)}
                    className="input-field pl-9 pr-3 py-1.5 text-sm bg-surface-50 border-surface-200 focus:bg-white"
                />
            </div>

            {/* Create button */}
            <button
                onClick={onCreateIssue}
                className="btn-primary py-1.5 px-3 text-sm shadow-sm"
            >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">New Task</span>
            </button>

            {/* Notifications */}
            <div className="relative" ref={notifRef}>
                <button
                    onClick={() => setShowNotifications(!showNotifications)}
                    className="relative btn-ghost p-2"
                >
                    <Bell className="w-5 h-5 text-surface-500" />
                    <span className="absolute top-1 right-1 w-2 h-2 bg-accent-red rounded-full" />
                </button>

                {showNotifications && (
                    <div className="dropdown-menu right-0 w-80">
                        <div className="px-4 py-3 border-b border-surface-100">
                            <h3 className="text-sm font-semibold text-surface-900">Notifications</h3>
                        </div>
                        <div className="py-2 max-h-80 overflow-y-auto">
                            <div className="px-4 py-6 text-center">
                                <Bell className="w-8 h-8 text-surface-300 mx-auto mb-2" />
                                <p className="text-sm text-surface-500">No new notifications</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* User menu */}
            <div className="relative" ref={userMenuRef}>
                <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-surface-50 transition-colors"
                >
                    {currentUser?.avatarUrl ? (
                        <img
                            src={currentUser.avatarUrl}
                            alt={currentUser.displayName}
                            className="w-7 h-7 rounded-full object-cover"
                        />
                    ) : (
                        <div className="avatar avatar-sm bg-brand-100 text-brand-700">
                            {currentUser?.displayName?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                    )}
                    <ChevronDown className="w-3.5 h-3.5 text-surface-400" />
                </button>

                {showUserMenu && (
                    <div className="dropdown-menu right-0 w-56">
                        <div className="px-4 py-3 border-b border-surface-100">
                            <p className="text-sm font-medium text-surface-900 truncate">
                                {currentUser?.displayName || 'User'}
                            </p>
                            <p className="text-xs text-surface-500 truncate">
                                {currentUser?.emailAddress || ''}
                            </p>
                        </div>
                        <div className="py-1">
                            <button
                                onClick={() => { onProfileClick(); setShowUserMenu(false); }}
                                className="dropdown-item w-full"
                            >
                                <UserIcon className="w-4 h-4" />
                                <span>Profile</span>
                            </button>
                            <button
                                onClick={() => { onSettingsClick(); setShowUserMenu(false); }}
                                className="dropdown-item w-full"
                            >
                                <Settings className="w-4 h-4" />
                                <span>Settings</span>
                            </button>
                            <button className="dropdown-item w-full">
                                <HelpCircle className="w-4 h-4" />
                                <span>Help & Support</span>
                            </button>
                        </div>
                        <div className="border-t border-surface-100 py-1">
                            <button
                                onClick={() => { onLogout(); setShowUserMenu(false); }}
                                className="dropdown-item w-full text-red-600 hover:bg-red-50"
                            >
                                <LogOut className="w-4 h-4" />
                                <span>Sign out</span>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </header>
    );
};
